import { accountStore } from "@features/account/account-store"
import { checkoutStore } from "@features/tickets/checkout-store"
import { useCheckoutConfig } from "@features/tickets/useCheckoutConfig"
import { useEffect } from "react"

const REFRESH_INTERVAL = 30_000

/**
 * Quietly re-checks the customer's sign-in status and cart every 30 seconds, so changes made in
 * another tab or in hosted checkout show up here. Nothing is shown while it refreshes.
 */
export function useBackgroundSessionRefresh() {
  const baseUrl = useCheckoutConfig().data?.webApiBaseUrl

  useEffect(() => {
    if (!baseUrl) return
    const url = baseUrl
    let lastRefresh = Date.now()
    let timer: ReturnType<typeof setTimeout> | undefined

    function refresh() {
      clearTimeout(timer)
      timer = undefined
      // Hidden tabs wait until they're visible again rather than polling Spektrix unseen.
      if (document.hidden) return
      lastRefresh = Date.now()
      // Signing in or out is already updating the customer.
      if (!accountStore.isPending) void accountStore.loadCustomer(url)
      void checkoutStore.refreshBasketInBackground()
      timer = setTimeout(refresh, REFRESH_INTERVAL)
    }

    function onVisibilityChange() {
      if (!document.hidden && Date.now() - lastRefresh >= REFRESH_INTERVAL) refresh()
    }

    // The header shows whether the customer is signed in, so read it once straight away.
    void accountStore.loadCustomer(url)
    timer = setTimeout(refresh, REFRESH_INTERVAL)
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      clearTimeout(timer)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [baseUrl])
}
